import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { MasonryGrid, type Photo } from "../components/portfolio/MasonryGrid";
import { Lightbox } from "../components/portfolio/Lightbox";
import { getProjectPhotos, getProjectByName, loadProjects, isShareProject } from "../data/projectLoader";
import { type Project } from "../types/project";
import { useLanguage } from "../contexts/LanguageContext";
import styles from "./Share.module.css";

export const Share: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const { t } = useLanguage();

  useEffect(() => {
    const loadProject = async () => {
      if (!projectId) {
        setError("No project specified");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Decode the URL-encoded project ID
        const decodedId = decodeURIComponent(projectId);
        console.log(`🔍 Looking for share project: ${decodedId}`);

        // Try to get photos directly - this will check the full manifest
        let projectPhotos = await getProjectPhotos(decodedId);

        if (projectPhotos.length > 0) {
          // We found photos, now get the project info
          let foundProject = await getProjectByName(decodedId);

          // If not found by name, try with share prefix
          if (!foundProject && !decodedId.startsWith('share-')) {
            foundProject = await getProjectByName(`share-${decodedId}`);
          }

          // If still not found, try to find by checking the path in photos
          if (!foundProject && projectPhotos.length > 0) {
            // Create a virtual project from the photos
            const firstPhoto = projectPhotos[0];
            foundProject = {
              name: decodedId.startsWith('share-') ? decodedId : `share-${decodedId}`,
              path: decodedId,
              imageCount: projectPhotos.length,
              coverImage: {
                filename: projectPhotos[0].filename || '',
                path: projectPhotos[0].src
              },
              images: projectPhotos.map(p => ({
                filename: p.filename || '',
                width: p.width,
                height: p.height,
                hasTransparency: false,
                originalFormat: 'jpeg',
                optimized: {
                  webp: {}
                }
              }))
            };
          }

          if (foundProject) {
            setProject(foundProject);
            setPhotos(projectPhotos);
          } else {
            setError(`Project "${decodedId}" not found`);
          }
        } else {
          setError(`Project "${decodedId}" not found`);
        }
      } catch (error) {
        console.error("Failed to load share project:", error);
        setError("Failed to load project");
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [projectId]);

  const handlePhotoClick = (photo: Photo) => {
    const index = photos.findIndex((p) => p.id === photo.id);
    setCurrentIndex(index);
    setSelectedPhoto(photo);
  };

  const handleClose = () => {
    setSelectedPhoto(null);
  };

  const handleNext = () => {
    if (photos.length === 0) return;
    const nextIndex = (currentIndex + 1) % photos.length;
    setCurrentIndex(nextIndex);
    setSelectedPhoto(photos[nextIndex]);
  };

  const handlePrev = () => {
    if (photos.length === 0) return;
    const prevIndex = (currentIndex - 1 + photos.length) % photos.length;
    setCurrentIndex(prevIndex);
    setSelectedPhoto(photos[prevIndex]);
  };

  const formatProjectName = (name: string): string => {
    // Remove share- prefix if present
    const cleanName = name.replace(/^share-/, '');
    return cleanName.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner} />
        <p>{t("common.loading")}</p>
      </div>
    );
  }

  if (error || !project || photos.length === 0) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorContent}>
          <h1 className={styles.errorTitle}>Project Not Found</h1>
          <p className={styles.errorMessage}>{error || "This project doesn't exist"}</p>
        </div>
      </div>
    );
  }

  const displayName = formatProjectName(project.name);

  return (
    <div className={styles.sharePage}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>{displayName}</h1>
          <p className={styles.subtitle}>
            {photos.length} {photos.length === 1 ? 'photo' : 'photos'}
          </p>
        </div>
      </div>

      <div className={styles.gridContainer}>
        <MasonryGrid
          photos={photos}
          columns={4}
          staggerDelay={60}
          onPhotoClick={handlePhotoClick}
        />
      </div>

      <Lightbox
        photo={selectedPhoto}
        onClose={handleClose}
        onNext={handleNext}
        onPrev={handlePrev}
      />
    </div>
  );
};
